/**
 * StatsBar - Animated stat visualization bar
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';

// Stat name mapping
const STAT_NAMES = {
  hp: { es: 'PS', en: 'HP' },
  attack: { es: 'Ataque', en: 'Attack' },
  defense: { es: 'Defensa', en: 'Defense' },
  'special-attack': { es: 'At. Esp.', en: 'Sp. Atk' },
  'special-defense': { es: 'Def. Esp.', en: 'Sp. Def' },
  speed: { es: 'Velocidad', en: 'Speed' }
};

// Max stat value for percentage calculation (base 255 is theoretical max)
const MAX_STAT = 255;

// Get color based on stat value
const getStatColor = (value) => {
  if (value < 50) return 'bg-red-500';
  if (value < 80) return 'bg-orange-500';
  if (value < 100) return 'bg-yellow-500';
  if (value < 130) return 'bg-lime-500';
  return 'bg-green-500';
};

export function StatsBar({ statName, value, maxValue = MAX_STAT, animated = true, delay = 0 }) {
  const { language } = useApp();
  const [mounted, setMounted] = useState(false);
  const percentage = Math.min((value / maxValue) * 100, 100);
  const color = getStatColor(value);
  const displayName = STAT_NAMES[statName]?.[language] || statName;
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  return (
    <div className="flex items-center gap-3" data-testid={`stat-bar-${statName}`}>
      <span className="w-20 text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">
        {displayName}
      </span>
      <span className="w-10 text-sm font-bold text-foreground text-right">
        {value}
      </span>
      <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
        {animated ? (
          <motion.div
            className={`h-full ${color} rounded-full`}
            initial={{ width: 0 }}
            animate={{ width: mounted ? `${percentage}%` : 0 }}
            transition={{ 
              duration: 0.8, 
              delay: delay,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          />
        ) : (
          <div 
            className={`h-full ${color} rounded-full`}
            style={{ width: `${percentage}%` }}
          />
        )}
      </div>
    </div>
  );
}

export function StatsPanel({ stats, animated = true }) {
  const { t } = useApp();
  
  // Calculate total stats
  const total = stats.reduce((sum, stat) => sum + stat.base_stat, 0);
  
  return (
    <div className="space-y-3" data-testid="stats-panel">
      {stats.map((stat, index) => (
        <StatsBar
          key={stat.stat.name}
          statName={stat.stat.name}
          value={stat.base_stat}
          animated={animated}
          delay={index * 0.1}
        />
      ))}
      
      {/* Total stats */}
      <div className="pt-3 border-t border-border">
        <div className="flex items-center gap-3">
          <span className="w-20 text-xs font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400">
            {t('total')}
          </span>
          <span className="w-10 text-sm font-bold text-violet-600 dark:text-violet-400 text-right">
            {total}
          </span>
          <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
            {animated ? (
              <motion.div
                className="h-full bg-gradient-to-r from-violet-500 to-rose-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min((total / 720) * 100, 100)}%` }}
                transition={{ 
                  duration: 1, 
                  delay: 0.6,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
              />
            ) : (
              <div 
                className="h-full bg-gradient-to-r from-violet-500 to-rose-500 rounded-full"
                style={{ width: `${Math.min((total / 720) * 100, 100)}%` }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatsBar;
